import memoize from 'lodash.memoize';
import * as api from '~lib/apiClient';
import logger from '~util/logger';
import mapConfigToBody from '~util/mapConfigToBody';

// ============================================
// ================= AIRLINES =================
// ============================================

/**
 * Get Airline Codes
 *
 * GET /airlinecodes
 */
export const _getAirlineCodes = async () => {
  const response = await api.getSecure('/airlinecodes');
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error(
    'An unexpected error occurred while fetching the list of supported airline codes'
  );
};

/**
 * Get Airline Codes (memoized)
 *
 * GET /airlinecodes
 */
export const getAirlineCodes = memoize(_getAirlineCodes);

/**
 * Get System Models By AirlineCode
 *
 * GET /airlinecodes/{airlineCode}/system/models
 */
export const _getAirlineSystemModels = async airlineCode => {
  const response = await api.getSecure(`/airlinecodes/${airlineCode}/system/models`);
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error(
    `An unexpected error occurred while fetching the system models for airline code ${airlineCode}`
  );
};

/**
 * Get System Models By AirlineCode (memoized)
 *
 * GET /airlinecodes/{airlineCode}/system/models
 */
export const getAirlineSystemModels = memoize(_getAirlineSystemModels);

// ============================================
// ================== MODELS ==================
// ============================================

/**
 * Get System Models
 *
 * GET /system/models
 */

/**
 * Get System Model Supported Aircards
 *
 * GET /system/models/{model}/aircards
 */

/**
 * Get System Model Supported Network Modes
 *
 * GET /system/models/{model}/networkmodes
 */

/**
 * Get System Model Profile
 *
 * GET /system/models/{model}/profile
 */
export const _getSystemModelProfile = async model => {
  const response = await api.getSecure(`/system/models/${model}/profile`);
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error('An unexpected error occurred while fetching the system model profile');
};

/**
 * Get System Model Profile (memoized)
 *
 * GET /system/models/{model}/profile
 */
export const getSystemModelProfile = memoize(_getSystemModelProfile);

/**
 * Get System Model Supported Subnet Groups
 *
 * GET /system/models/{model}/subnetgroups
 */

// =============================================
// ==================== NAI ====================
// =============================================

/**
 * Activate System
 *
 * POST /system/nai/{nai}/activate
 */
export const activateSystem = async nai => {
  const response = await api.postSecure(`/system/nai/${nai}/activate`);
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error('An unexpected error occurred while activating the system');
};

/**
 * Deactivate System
 *
 * POST /system/nai/{nai}/deactivate
 */
export const deactivateSystem = async nai => {
  const response = await api.postSecure(`/system/nai/${nai}/deactivate`);
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error('An unexpected error occurred while deactivating the system');
};

/**
 * DeProvision System
 *
 * POST /system/nai/{nai}/deactivate
 */
export const deprovisionSystem = async nai => {
  const response = await api.postSecure(`/system/nai/${nai}/deprovision`);
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error('An unexpected error occurred while deactivating the system');
};

/**
 * Get System Configuration
 *
 * GET /system/nai/{nai}/configuration
 */
export const getSystemConfig = async nai => {
  const response = await api.getSecure(`/system/nai/${nai}/configuration`);
  if (!!response.success || response?.status === 404) {
    return response?.status === 404 ? null : response?.data ?? null;
  }
  throw new Error('An unexpected error occurred while fetching the current system configuration');
};

/**
 * Get System History
 *
 * GET /system/nai/{nai}/history
 */
export const getSystemHistory = async nai => {
  const response = await api.getSecure(`/system/nai/${nai}/history`);

  return response?.status === 404 ? null : response?.data ?? null;
};

// ==============================================
// =================== SYSTEM ===================
// ==============================================

/**
 * Change Airline Code
 *
 * PUT /system/provision
 */
export const changeAirlineCode = async (existingConfig, airlineCode) => {
  const nai = existingConfig?.nai;

  if (!!nai && !!airlineCode) {
    const body = mapConfigToBody(existingConfig);
    body.airlineCode = airlineCode;
    const response = await api.putSecure('/system/provision', body);
    if (!!response?.success && !!response?.data) return response.data;
  } else {
    logger.error(
      `changeAirlineCode - missing parameter: existingConfig: ${JSON.stringify(
        existingConfig
      )} airlineCode: ${airlineCode}`
    );
  }
  throw new Error('An unexpected error occurred while changing the system airline code');
};

/**
 * Change Subnet Group
 *
 * PUT /system/provision
 */
export const changeSubnetGroup = async (existingConfig, subnetGroup) => {
  const nai = existingConfig?.nai;

  if (!!nai && !!subnetGroup) {
    const body = mapConfigToBody(existingConfig);
    body.subnetGroup = subnetGroup;
    const response = await api.putSecure('/system/provision', body);
    if (!!response?.success && !!response?.data) return response.data;
  } else {
    logger.error(
      `changeSubnetGroup - missing parameter: existingConfig: ${JSON.stringify(
        existingConfig
      )} subnetGroup: ${subnetGroup}`
    );
  }
  throw new Error('An unexpected error occurred while changing the system subnet group');
};

/**
 * Convert System Model
 *
 * POST /system/provision
 */
export const convertSystemModel = async (existingConfig, systemModelName, aircards) => {
  const nai = existingConfig?.nai;

  if (!!nai && !!systemModelName && !!aircards?.length) {
    const body = mapConfigToBody(existingConfig);
    body.aircards = aircards;
    body.model = systemModelName;
    body.subnetGroup = systemModelName;
    const response = await api.postSecure('/system/provision', body);
    if (!!response?.success && !!response?.data) return response.data;
  } else {
    logger.error(
      `convertSystemModel - missing parameter: existingConfig: ${JSON.stringify(
        existingConfig
      )} systemModelName: ${systemModelName} aircards: ${JSON.stringify(aircards)}`
    );
  }
  throw new Error(
    `An unexpected error occurred while converting the system model to ${systemModelName}`
  );
};

/**
 * Replace an Aircard
 *
 * PUT /system/provision
 */
export const replaceAircard = async (existingConfig, newAircard) => {
  const nai = existingConfig?.nai;

  if (!!nai && !!newAircard) {
    const body = mapConfigToBody(existingConfig);
    const existingAircards = body?.aircards;
    if (!existingAircards?.length) {
      throw new Error('No aircards found in existing configuration at time of aircard replacement');
    }
    const newAircards = mergeNewAircard(existingAircards, newAircard);
    body.aircards = newAircards;
    const response = await api.putSecure('/system/provision', body);
    if (!!response?.success && !!response?.data) return response.data;
  } else {
    logger.error(
      `replaceAircard - missing parameter: existingConfig: ${JSON.stringify(
        existingConfig
      )} newAircard: ${JSON.stringify(newAircard)}`
    );
  }
  throw new Error('An unexpected error occurred while replacing the system aircard');
};

/**
 * Provision a New System
 *
 * POST /system/provision
 */
export const provisionSystem = async config => {
  const response = await api.postSecure('/system/provision', config);
  if (!!response?.success && !!response?.data) return response.data;
  throw new Error(
    `An unexpected error occurred while provisioning the system. Message: ${response?.data?.message}`
  );
};

const mergeNewAircard = (existingAircards, newAircard) => {
  let aircardFound = false;

  const reconciledAircards = existingAircards.map(aircard => {
    if (aircard.id === newAircard.id) {
      aircardFound = true;
      return { ...newAircard, replaceID: newAircard.id };
    }
    return aircard;
  });

  if (!aircardFound) {
    throw new Error(`No existing aircard found matching replaced aircard id "${newAircard.id}"`);
  }
  return reconciledAircards;
};
